import React, { useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Box, Typography, IconButton, Divider
} from '@mui/material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { X, Check } from 'lucide-react';

const validationSchema = yup.object({
  hours: yup.number().required('Hours is required').positive().integer(),
  distance: yup.number().required('Distance is required').min(0),
  extraKm: yup.number().required('Extra KM is required').min(0),
  extraAmount: yup.number().required('Extra Amount is required').min(0),
});

const ServicetypeUpdateModal = ({ openDialog, handleClose, data, onSubmit }) => {
  const formik = useFormik({
    initialValues: {
      hours: '',
      distance: '',
      extraKm: '',
      extraAmount: '',
    },
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: (values) => {
      onSubmit({
        hours: values.hours,
        distance: Number(values.distance),
        extraKm: Number(values.extraKm),
        extraAmount: Number(values.extraAmount),
      });
    },
  });

  // Pre-fill form when data changes
  useEffect(() => {
    if (data) {
      formik.setValues({
        hours: data.hours || '',
        distance: data.distance || '',
        extraKm: data.extraKm || '',
        extraAmount: data.extraAmount || '',
      });
    }
  }, [data]); // eslint-disable-line

  return (
    <Dialog open={openDialog} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      
      {/* Header */}
      <DialogTitle sx={{ p: 2.5, pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography sx={{ fontSize: 16, fontWeight: 600, color: '#111827' }}>UPDATE HOUR</Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#6B7280' }}>Hours</Typography>
              <Box component="span" sx={{ px: 1.5, py: 0.3, border: '1px solid #EC4899', borderRadius: 20, fontSize: 12, color: '#EC4899' }}>
                Hours List
              </Box>
            </Box>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <X size={18} />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider />

      <form onSubmit={formik.handleSubmit}>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            
            {/* Hours */}
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#374151', mb: 0.8 }}>Hours</Typography>
              <TextField
                fullWidth
                size="small"
                name="hours"
                value={formik.values.hours}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.hours && Boolean(formik.errors.hours)}
                helperText={formik.touched.hours && formik.errors.hours}
                sx={{ '&.MuiOutlinedInput-root': { borderRadius: 1.5 } }}
              />
            </Box>

            {/* Distance */}
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#374151', mb: 0.8 }}>Distance</Typography>
              <TextField
                fullWidth
                size="small"
                name="distance"
                type="number"
                value={formik.values.distance}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.distance && Boolean(formik.errors.distance)}
                helperText={formik.touched.distance && formik.errors.distance}
                sx={{ '&.MuiOutlinedInput-root': { borderRadius: 1.5 } }}
              />
            </Box>

            {/* Extra Km */}
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#374151', mb: 0.8 }}>Extra Km</Typography>
              <TextField
                fullWidth
                size="small"
                name="extraKm"
                type="number"
                value={formik.values.extraKm}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.extraKm && Boolean(formik.errors.extraKm)}
                helperText={formik.touched.extraKm && formik.errors.extraKm}
                sx={{ '&.MuiOutlinedInput-root': { borderRadius: 1.5 } }}
              />
            </Box>

            {/* Extra Amount */}
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#374151', mb: 0.8 }}>Extra Amount</Typography>
              <TextField
                fullWidth
                size="small"
                name="extraAmount"
                type="number"
                value={formik.values.extraAmount}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.extraAmount && Boolean(formik.errors.extraAmount)}
                helperText={formik.touched.extraAmount && formik.errors.extraAmount}
                sx={{ '&.MuiOutlinedInput-root': { borderRadius: 1.5 } }}
              />
            </Box>

          </Box>
        </DialogContent>

        <Divider />

        {/* Footer Buttons */}
        <DialogActions sx={{ p: 2.5 }}>
          <Button 
            onClick={handleClose} 
            variant="contained" 
            sx={{ backgroundColor: '#6B7280', color: '#fff', textTransform: 'none', '&:hover': { backgroundColor: '#4B5563' } }}
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            startIcon={<Check size={16} />}
            sx={{ backgroundColor: '#10B981', color: '#fff', textTransform: 'none', '&:hover': { backgroundColor: '#059669' } }}
          >
            Update Hours
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ServicetypeUpdateModal;